import { useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Link, useParams } from "wouter"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { api, ApiRequestError } from "@/lib/api"
import { inputClass, submitBtnClass } from "@/lib/styleClasses"

interface ResetPasswordForm {
    password: string
    confirmPassword: string
}

export const AdminResetPasswordPage = () => {
    const { t } = useTranslation()
    const { token } = useParams<{ token: string }>()
    const [success, setSuccess] = useState(false)
    const [resetError, setResetError] = useState<string | null>(null)

    const { register, handleSubmit, watch, formState: { errors, isSubmitting } } = useForm<ResetPasswordForm>()
    const password = watch("password")

    const onSubmit = async (data: ResetPasswordForm) => {
        setResetError(null)
        try {
            await api.post("/auth/reset-password", { token, password: data.password })
            setSuccess(true)
        } catch (error) {
            const message = error instanceof ApiRequestError ? error.message : t("admin.resetPassword.toastFailed")
            setResetError(message)
        }
    }

    return (
        <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-[40px] border border-slate-100 shadow-xl p-10 w-full max-w-md">
                <div className="mb-8 text-center">
                    <h1 className="text-3xl font-black tracking-tight text-slate-900 mb-2">{t("admin.resetPassword.title")}</h1>
                    <p className="text-slate-500 font-bold">{t("admin.resetPassword.subtitle")}</p>
                </div>

                {success ? (
                    <div className="text-center space-y-4">
                        <p className="text-slate-700 font-bold">{t("admin.resetPassword.successMessage")}</p>
                        <Link to="/admin/login" className="inline-block text-sm font-black text-brand hover:text-brand-dark transition-colors">
                            {t("admin.resetPassword.signIn")}
                        </Link>
                    </div>
                ) : (
                    <>
                        {resetError && (
                            <div className="mb-4 rounded-2xl bg-red-50 border border-red-100 px-4 py-3 text-sm font-bold text-red-600">
                                {resetError}
                            </div>
                        )}

                        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                            <div className="space-y-1">
                                <label className="text-xs font-black uppercase tracking-widest text-slate-600">
                                    {t("admin.resetPassword.newPassword")}
                                </label>
                                <Input
                                    type="password"
                                    placeholder="••••••••"
                                    className={inputClass}
                                    error={errors.password?.message}
                                    {...register("password", {
                                        required: t("admin.resetPassword.passwordRequired"),
                                        minLength: { value: 8, message: t("admin.resetPassword.passwordMin") },
                                    })}
                                />
                            </div>

                            <div className="space-y-1">
                                <label className="text-xs font-black uppercase tracking-widest text-slate-600">
                                    {t("admin.resetPassword.confirmPassword")}
                                </label>
                                <Input
                                    type="password"
                                    placeholder="••••••••"
                                    className={inputClass}
                                    error={errors.confirmPassword?.message}
                                    {...register("confirmPassword", {
                                        required: t("admin.resetPassword.confirmPasswordRequired"),
                                        validate: (value) =>
                                            value === password || t("admin.resetPassword.passwordsMustMatch"),
                                    })}
                                />
                            </div>

                            <Button
                                type="submit"
                                disabled={isSubmitting}
                                className={submitBtnClass}
                            >
                                {isSubmitting ? t("admin.resetPassword.resetting") : t("admin.resetPassword.reset")}
                            </Button>
                        </form>

                        <p className="mt-6 text-center text-sm text-slate-500">
                            <Link to="/admin/login" className="font-black text-brand hover:text-brand-dark transition-colors">
                                {t("admin.resetPassword.backToLogin")}
                            </Link>
                        </p>
                    </>
                )}
            </div>
        </div>
    )
}
